import type { NavMainItem } from '@/components/nav-main'
import { cn } from '@/lib/utils'
import * as React from 'react'

export interface TopNavLayoutProps {
  navItems: NavMainItem[]
  navuser: React.ReactNode
  navorganisation: React.ReactNode
  navextra?: React.ReactNode
  onNavigate: (url: string) => void
  appName?: string
  logo?: React.ReactNode
  children: React.ReactNode
}

export function TopNavLayout({
  navItems,
  navuser,
  navorganisation,
  navextra,
  onNavigate,
  appName = 'Reports',
  logo,
  children
}: TopNavLayoutProps) {
  const renderedLogo = logo ?? (
    <span className='text-foreground text-sm font-semibold'>{appName}</span>
  )

  return (
    <div className='bg-background flex min-h-screen flex-col'>
      {/* Desktop header */}
      <header className='border-border bg-background/95 supports-backdrop-filter:bg-background/60 sticky top-0 z-40 hidden items-center border-b py-3.5 backdrop-blur md:flex print:hidden'>
        <div className='flex w-full items-center justify-between px-6'>
          {/* Logo */}
          <div className='flex shrink-0 items-center' aria-label={appName}>
            {renderedLogo}
          </div>

          {/* Nav links (centered) */}
          <nav className='flex items-center gap-10'>
            {navItems.map((item) => (
              <button
                key={item.url}
                type='button'
                onClick={() => onNavigate(item.url)}
                className={cn(
                  'text-sm font-medium transition-colors',
                  item.isActive
                    ? 'text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                {item.title}
              </button>
            ))}
          </nav>

          {/* Right side */}
          <div className='flex items-center gap-2'>
            {navextra}
            {navorganisation}
            {navuser}
          </div>
        </div>
      </header>

      {/* Mobile top bar */}
      <header className='border-border bg-background/95 supports-[backdrop-filter]:bg-background/60 sticky top-0 z-40 flex h-12 items-center border-b backdrop-blur md:hidden print:hidden'>
        <div className='flex w-full items-center justify-between px-3'>
          <div className='flex items-center gap-2'>{renderedLogo}</div>
          <div className='flex items-center gap-2'>
            {navextra}
            {navuser}
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className='flex-1'>{children}</main>

      {/* Mobile bottom tab bar */}
      <nav
        className='border-border bg-background/95 supports-[backdrop-filter]:bg-background/60 fixed right-0 bottom-0 left-0 z-40 flex items-center justify-around border-t backdrop-blur md:hidden print:hidden'
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {navItems.map((item) => {
          const Icon = item.icon
          return (
            <button
              key={item.url}
              type='button'
              onClick={() => onNavigate(item.url)}
              className={cn(
                'flex flex-1 flex-col items-center gap-0.5 py-2 text-[10px] font-medium transition-colors',
                item.isActive ? 'text-primary' : 'text-muted-foreground'
              )}
            >
              {Icon && <Icon className='h-5 w-5' />}
              <span>{item.title}</span>
            </button>
          )
        })}
      </nav>

      {/* Spacer for bottom tab bar on mobile */}
      <div className='h-16 md:hidden print:hidden' />
    </div>
  )
}
